import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import LogoImg from "../../assets/images/logo3PL.svg";
import PhonePeQr from "../../assets/images/phonepe-qr.jpg";
import "./SellerPayment.css";

export const PAYMENT_DETAILS = {
  upiAmount: "₹1,000",
  payeeName: "JAYESH SALVI",
  accountName: "Minrak Worldwide Private Limited",
  accountNumber: "3411518423",
  ifsc: "KKBK0000812",
  registrationFee: "₹1,000",
  gst: "₹180",
  total: "₹1,180",
  email: "jayesh@3plvision.com",
  phone: "+91 99288 17346",
  whatsappNumber: "919928817346",
};

export const EMAIL_LINK = `mailto:${PAYMENT_DETAILS.email}?subject=${encodeURIComponent(
  "3PL Vision Seller Payment Confirmation"
)}`;

export const WHATSAPP_LINK = `https://wa.me/${PAYMENT_DETAILS.whatsappNumber}?text=${encodeURIComponent(
  "Hello, I have completed my 3PL Vision seller payment. I will share the payment proof and business details."
)}`;

const COPIED_RESET_MS = 1800;

// navigator.clipboard is unavailable on non-secure (http) origins, so fall back to execCommand.
const copyText = async (text) => {
  if (navigator.clipboard?.writeText && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  textarea.remove();
  if (!copied) throw new Error("Copy failed");
};

const CopyButton = ({ value, label }) => {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const handleCopy = async () => {
    try {
      await copyText(value);
      setCopied(true);
    } catch (error) {
      window.prompt("Copy this value:", value);
    }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), COPIED_RESET_MS);
  };

  return (
    <button type="button" className="sp-copy" onClick={handleCopy} aria-label={label}>
      {copied ? "Copied!" : "Copy"}
    </button>
  );
};

CopyButton.propTypes = {
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};

const Line = ({ label, children }) => (
  <div className="sp-line">
    <span>{label}</span>
    {children}
  </div>
);

Line.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const SellerPayment = () => {
  const d = PAYMENT_DETAILS;

  return (
    <div className="sp-page">
      <header className="sp-top">
        <div className="sp-top-inner">
          <img className="sp-logo" src={LogoImg} alt="3PL Vision" />
          <small>Seller Payment Portal</small>
        </div>
      </header>

      <main className="sp-main">
        <div className="sp-intro">
          <div className="sp-eyebrow">Seller Registration</div>
          <h1>Complete your payment</h1>
          <p>
            Choose your preferred payment method below. After paying, send us your transaction reference or screenshot
            so our team can verify your payment.
          </p>
        </div>

        <section className="sp-grid" aria-label="Payment methods">
          <article className="sp-card">
            <span className="sp-pill">Option 1 · PhonePe / UPI</span>
            <h2>Scan &amp; Pay</h2>
            <p className="sp-muted">Use your UPI app to scan the QR code and pay the amount shown.</p>
            <div className="sp-price">{d.upiAmount}</div>
            <div className="sp-muted">QR payment amount</div>
            <div className="sp-qr-wrap">
              <img className="sp-qr" src={PhonePeQr} alt={`PhonePe payment QR code for ${d.payeeName}`} />
            </div>
            <p className="sp-recipient">
              Payee name: <b>{d.payeeName}</b>
              <br />
              Verify the payee name before paying.
            </p>
          </article>

          <article className="sp-card">
            <span className="sp-pill sp-green">Option 2 · GST Invoice</span>
            <h2>Bank Transfer</h2>
            <p className="sp-muted">
              For payment with an 18% GST invoice, transfer the total below to the company account.
            </p>
            <div className="sp-details">
              <Line label="Account name">
                <strong>{d.accountName}</strong>
              </Line>
              <Line label="Account number">
                <div className="sp-bank-row">
                  <strong>{d.accountNumber}</strong>
                  <CopyButton value={d.accountNumber} label="Copy account number" />
                </div>
              </Line>
              <Line label="IFSC code">
                <div className="sp-bank-row">
                  <strong>{d.ifsc}</strong>
                  <CopyButton value={d.ifsc} label="Copy IFSC code" />
                </div>
              </Line>
              <Line label="Registration fee">
                <strong>{d.registrationFee}</strong>
              </Line>
              <Line label="GST (18%)">
                <strong>{d.gst}</strong>
              </Line>
              <div className="sp-total">
                <span>Total payable</span>
                <strong>{d.total}</strong>
              </div>
            </div>
            <p className="sp-muted">
              For the GST invoice, include your registered business name, GSTIN and billing address when sharing payment
              confirmation.
            </p>
          </article>
        </section>

        <section className="sp-after">
          <h2>Already paid?</h2>
          <p>
            Send your payment screenshot or transaction reference with your business name.
            <br />
            For a GST invoice, also include your GSTIN and billing address.
          </p>
          <div className="sp-actions">
            <a className="sp-btn" href={EMAIL_LINK}>
              ✉ &nbsp; Email payment proof
            </a>
            <a className="sp-btn sp-alt" target="_blank" rel="noopener noreferrer" href={WHATSAPP_LINK}>
              WhatsApp payment proof
            </a>
          </div>
          <p className="sp-contact">
            {d.email} &nbsp; · &nbsp; {d.phone}
          </p>
        </section>

        <p className="sp-footnote">
          Please verify the payment recipient details before making a transfer. Payment is confirmed only after
          transaction verification. GST invoicing and applicable tax treatment are subject to the seller&apos;s
          transaction and relevant tax requirements.
        </p>
      </main>

      <footer className="sp-footer">© 3PL Vision. All rights reserved.</footer>
    </div>
  );
};

export default SellerPayment;
